import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useFocus, useInteractions, useClick, useDismiss, flip, autoUpdate, size } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import Icons from '../../../../Elements/Icons';
import { ANIMATION_DURATION_MS } from './constants';
import { Button, ButtonWrapper, Wrapper, CaretWrapper } from './styled';
function selectPlacement(align) {
    switch (align) {
        case 'right':
            return 'bottom-start';
        case 'left':
            return 'bottom-end';
        default:
            return 'bottom-start';
    }
}
const marginFromBottomScreenEdge = 8;
const maxMenuHeight = 375;
export const FilterButton = ({ id, className, dataTestId, children, dropdown, disabled = false, zIndex = 10, align = 'right', isShown, setIsShown, recover, dropdownWidth, }) => {
    const theme = useTheme();
    const floatingPlacement = useMemo(() => selectPlacement(align), [align]);
    const middleware = useMemo(() => [
        offset(5),
        flip({
            fallbackPlacements: ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
        }),
        size({
            apply({ availableHeight, elements }) {
                elements.floating.style.maxHeight =
                    availableHeight < maxMenuHeight ? `${availableHeight - marginFromBottomScreenEdge}px` : `${maxMenuHeight}px`;
            },
        }),
    ], []);
    const { refs, floatingStyles, context, placement } = useFloating({
        open: isShown,
        onOpenChange: setIsShown,
        whileElementsMounted: autoUpdate,
        placement: floatingPlacement,
        middleware,
    });
    const focus = useFocus(context);
    const click = useClick(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, focus]);
    useDismiss(context, {
        enabled: isShown,
    });
    const isTop = useMemo(() => placement.startsWith('top'), [placement]);
    const stopPropagation = useCallbackRef((evt) => evt.stopPropagation());
    const caretColor = useMemo(() => (disabled ? theme.colors.grey30_1 : theme.colors.grey70_1), [disabled, theme.colors.grey30_1, theme.colors.grey70_1]);
    const memoizedFloatingStyles = useMemo(() => ({
        ...floatingStyles,
        zIndex,
    }), [floatingStyles, zIndex]);
    return (_jsxs(_Fragment, { children: [ReactDOM.createPortal(_jsx(CSSTransition, { in: isShown, transitionAppear: true, timeout: ANIMATION_DURATION_MS, unmountOnExit: true, children: _jsx(Wrapper, { ref: refs.setFloating, ...getFloatingProps(), style: memoizedFloatingStyles, "$visible": isShown, "$isTop": isTop, width: dropdownWidth, "data-testid": 'filter-content-wrapper', children: dropdown }) }), document.body), _jsx(ButtonWrapper, { onClick: stopPropagation, children: recover ? (_jsxs(Button, { className: className, type: 'button', children: [children, recover] })) : (_jsxs(Button, { id: id, className: className, "data-testid": dataTestId, disabled: disabled, 
                    // eslint-disable-next-line react-hooks/refs
                    ref: refs.setReference, ...getReferenceProps(), type: 'button', children: [children, _jsx(CaretWrapper, { children: _jsx(Icons.Caret, { size: 16, align: isShown ? 'top' : 'bottom', color: caretColor }) })] })) })] }));
};
